'use client';

import { useAuiState } from '@assistant-ui/react';

import { useHitl } from './hitl-provider';

interface HitlPartShape {
  type?: string;
  toolName?: string;
  toolCallId?: string;
  result?: { status?: string } | unknown;
  approval?: { approved?: boolean; resolution?: string };
}

export type HitlCallLane = 'human' | 'approval';

/**
 * "toolCallId|resultStatus|lane" for the HITL call among the given message parts, or '' when
 * there is none. Covers both lanes: a client-executed `type: "human"` tool call (resolved via
 * `addResult`), and a server-executed approval gate (`part.approval`, resolved via
 * `respondToApproval`).
 */
export function findHitlCallKeyInParts({
  parts,
  humanToolNames,
}: {
  parts: readonly unknown[];
  humanToolNames: ReadonlySet<string>;
}): string {
  for (const part of parts as ReadonlyArray<HitlPartShape>) {
    if (part.type !== 'tool-call' || !part.toolCallId) continue;
    if (part.approval) {
      const resolved = part.approval.approved !== undefined || !!part.approval.resolution;
      return `${part.toolCallId}|${resolved ? 'responded' : ''}|approval`;
    }
    if (part.toolName && humanToolNames.has(part.toolName)) {
      const status = (part.result as { status?: string } | undefined)?.status ?? '';
      return `${part.toolCallId}|${status}|human`;
    }
  }
  return '';
}

/**
 * `findHitlCallKeyInParts` for the current message. A joined string keeps `useAuiState`
 * referentially stable across re-renders.
 */
export function useHitlCallKey(): string {
  const { humanToolNames } = useHitl();
  return useAuiState(({ message }) =>
    findHitlCallKeyInParts({ parts: message?.parts ?? [], humanToolNames }),
  );
}

export function parseHitlCallKey(key: string): {
  toolCallId: string;
  resultStatus: string;
  lane: HitlCallLane | '';
} {
  const [toolCallId = '', resultStatus = '', lane = ''] = key.split('|');
  return { toolCallId, resultStatus, lane: lane as HitlCallLane | '' };
}

/**
 * True while a HITL action awaits the user in the current message: an unresolved human-tool call
 * OR a pending approval gate. This is the "run is paused for human input" signal — it keeps the
 * activity trail live and hides the message's finished-state footer (timestamp + action bar).
 * We key on the unresolved call rather than the message's `requires-action` status because the
 * AI-SDK runtime treats a human-tool-paused message as complete-with-a-pending-tool, so the
 * status alone is unreliable.
 */
export function useAwaitingHitlAction(): boolean {
  const { toolCallId, resultStatus } = parseHitlCallKey(useHitlCallKey());
  return toolCallId !== '' && !resultStatus;
}
